import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Filter, X } from 'lucide-react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  InstantSearchProvider,
  SearchBox,
  Hits,
  RefinementList,
  Pagination,
  SortBy,
  RangeSlider,
  Stats,
  CurrentRefinements,
} from '@/components/search';

const SORT_OPTIONS = [
  { value: 'sequences_v2', label: 'Relevance' },
  { value: 'sequences_v2/sort/download_count:desc', label: 'Most Downloaded' },
  { value: 'sequences_v2/sort/created_at:desc', label: 'Newest First' },
  { value: 'sequences_v2/sort/price:asc', label: 'Price: Low to High' },
  { value: 'sequences_v2/sort/price:desc', label: 'Price: High to Low' },
];

const SequencesInstantSearch = () => {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <>
      <Helmet>
        <title>Light Display Sequences - Search & Download</title>
        <meta 
          name="description" 
          content="Search and download holiday light display sequences. Find Christmas, Halloween, and other holiday light shows." 
        />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        
        <InstantSearchProvider indexName="sequences_v2" routing={true}>
          <main className="flex-1">
            {/* Search Header */}
            <div className="bg-muted/30 border-b">
              <div className="container py-6">
                <div className="max-w-4xl mx-auto">
                  <h1 className="text-3xl font-bold mb-4">Light Display Sequences</h1>
                  <div className="flex gap-2">
                    <SearchBox 
                      placeholder="Search songs, artists, genres..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="shrink-0 lg:hidden"
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                    </Button>
                  </div>

                  {/* Search Stats and Sort (match Displays layout) */}
                  <div className="flex items-center justify-between mt-4 text-sm">
                    <Stats />
                    <SortBy items={SORT_OPTIONS} />
                  </div>
                </div>
              </div>
            </div>

            {/* Active Filters */}
            <div className="container py-4">
              <CurrentRefinements />
            </div>

            <div className="container">
              <div className="flex gap-6">
                {/* Filters Sidebar (match Displays) */}
                <aside className={cn(
                  "w-80 shrink-0 sticky top-4 h-fit",
                  !showFilters && "hidden lg:block"
                )}>
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Filters</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {/* Software Filter */}
                      <RefinementList
                        attribute="software_type"
                        title="Software"
                        expanded={true}
                        limit={50}
                      />
                      
                      <Separator className="my-4" />
                      
                      {/* Holiday Filter */}
                      <RefinementList
                        attribute="holiday_type"
                        title="Holiday"
                        expanded={true}
                        limit={50}
                      />
                      
                      <Separator className="my-4" />
                      
                      {/* Genre Filter */}
                      <RefinementList
                        attribute="genres"
                        title="Genres"
                        expanded={true}
                        limit={50}
                        searchable={true}
                        searchablePlaceholder="Search genres..."
                      />
                      
                      <Separator className="my-4" />
                      
                      {/* Artist Filter */}
                      <RefinementList
                        attribute="artist_name"
                        title="Artists"
                        expanded={false}
                        limit={50}
                        searchable={true}
                        searchablePlaceholder="Search artists..."
                      />
                      
                      <Separator className="my-4" />

                      {/* Price Range */}
                      <RangeSlider
                        attribute="price"
                        title="Price Range"
                        expanded={true}
                        min={0}
                        max={100}
                        step={5}
                        precision={2}
                      />

                      <Separator className="my-4" />

                      {/* Free Filter */}
                      <RefinementList
                        attribute="is_free"
                        title="Price Type"
                        expanded={true}
                        limit={10}
                      />

                      <Separator className="my-4" />

                      {/* Featured Filter -- removed for sequences_v2 since not a facet */}
                    </CardContent>
                  </Card>
                </aside>

                {/* Results */}
                <div className="flex-1 min-w-0 space-y-6">
                  <Hits />
                  <Pagination className="pb-8" />
                </div>
              </div>

              {/* Mobile Filters Overlay */}
              {showFilters && (
                <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-xs">
                  <div className="fixed inset-y-0 left-0 w-80 bg-background border-r overflow-y-auto">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">Filters</h2>
                        <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-4">
                        <RefinementList 
                          attribute="software_type"
                          title="Software"
                          expanded={true}
                          limit={50}
                        />
                        <RefinementList
                          attribute="holiday_type"
                          title="Holiday"
                          expanded={true}
                          limit={50}
                        />
                        <RefinementList
                          attribute="genres"
                          title="Genres"
                          expanded={true}
                          limit={50}
                          searchable={true}
                        />
                        <RefinementList
                          attribute="artist_name"
                          title="Artists"
                          expanded={false}
                          limit={50}
                          searchable={true}
                        />
                        <RangeSlider
                          attribute="price"
                          title="Price Range"
                          expanded={true}
                          min={0}
                          max={100}
                          step={5}
                        />
                        <RefinementList
                          attribute="is_free"
                          title="Price Type"
                          expanded={true}
                        />
                        {/* Featured removed for sequences_v2 */}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </InstantSearchProvider>
        
        <Footer />
      </div>
    </>
  );
};

export default SequencesInstantSearch;